import { c as _c } from "react/compiler-runtime";
import { FC, useEffect, useLayoutEffect, useRef } from 'react';
import Filters from '@/components/CaseStudiesPage/Filters';
import { Typography } from '@/components/shared/Typography';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
interface Props {
  services: string[];
  industries: string[];
  selectedServices: string[];
  selectedIndustries: string[];
  onToggleFilter: (category: 'service' | 'industry', value: string) => void;
  onClearFilters: (category: 'service' | 'industry') => void;
  onClose: () => void;
}
const MobileFiltersSheet: FC<Props> = t0 => {
  const $ = _c(29);
  const {
    services,
    industries,
    selectedServices,
    selectedIndustries,
    onToggleFilter,
    onClearFilters,
    onClose
  } = t0;
  const filtersSheetRef = useRef(null);
  const overlayRef = useRef(null);
  const closeHandlerRef = useRef(null);
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);
  const isDragging = useRef(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const sheet = filtersSheetRef.current;
      const overlay = overlayRef.current;
      if (!sheet || !overlay) {
        return;
      }
      overlay.style.display = "block";
      sheet.style.display = "flex";
      gsap.set(overlay, {
        opacity: 0
      });
      gsap.set(sheet, {
        y: "100%",
        opacity: 0,
        clearProps: "transform"
      });
      gsap.to(overlay, {
        opacity: 1,
        duration: 0.3,
        ease: "power2.out"
      });
      gsap.to(sheet, {
        y: 0,
        opacity: 1,
        duration: 0.4,
        ease: "power2.out"
      });
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[2] !== onClose) {
    t3 = t4 => {
      const duration = t4 === undefined ? 0.4 : t4;
      const sheet_0 = filtersSheetRef.current;
      const overlay_0 = overlayRef.current;
      if (!sheet_0) {
        return;
      }
      gsap.to(sheet_0, {
        y: "100%",
        duration,
        ease: "power2.in",
        onComplete: () => {
          onClose();
        }
      });
      if (overlay_0) {
        gsap.to(overlay_0, {
          opacity: 0,
          duration,
          ease: "power2.in"
        });
      }
    };
    $[2] = onClose;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const animateClose = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      const target = e.target as HTMLElement;
      const sheet_1 = filtersSheetRef.current;
      if (!sheet_1) {
        return;
      }
      const isHandle = target.closest(`.${css.sheetHandle}`);
      const sheetRect = sheet_1.getBoundingClientRect();
      const touchY = e.touches[0].clientY;
      const isTopPart = sheet_1.contains(target) && touchY >= sheetRect.top && touchY <= sheetRect.top + 60;
      if (!isHandle && !isTopPart) {
        return;
      }
      touchStartY.current = touchY;
      touchCurrentY.current = touchStartY.current;
      isDragging.current = true;
      e.preventDefault();
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleTouchStart = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_0 => {
      if (!isDragging.current) {
        return;
      }
      const sheet_2 = filtersSheetRef.current;
      if (!sheet_2) {
        return;
      }
      touchCurrentY.current = e_0.touches[0].clientY;
      const deltaY = touchCurrentY.current - touchStartY.current;
      if (deltaY > 0) {
        gsap.set(sheet_2, {
          y: `${deltaY}px`,
          clearProps: "none"
        });
        const overlay_1 = overlayRef.current;
        if (overlay_1) {
          const opacity = Math.max(0, 1 - deltaY / 200);
          gsap.set(overlay_1, {
            opacity
          });
        }
      }
      e_0.preventDefault();
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleTouchMove = t5;
  let t6;
  if ($[6] !== animateClose) {
    t6 = () => {
      if (!isDragging.current) {
        return;
      }
      const sheet_3 = filtersSheetRef.current;
      const overlay_2 = overlayRef.current;
      if (!sheet_3) {
        return;
      }
      isDragging.current = false;
      const deltaY_0 = touchCurrentY.current - touchStartY.current;
      if (deltaY_0 > 50) {
        const sheetHeight = sheet_3.offsetHeight;
        const remainingDistance = sheetHeight - deltaY_0;
        const duration_0 = Math.max(0.2, remainingDistance / sheetHeight * 0.4);
        animateClose(duration_0);
      } else {
        gsap.to(sheet_3, {
          y: 0,
          duration: 0.3,
          ease: "power2.out"
        });
        if (overlay_2) {
          gsap.to(overlay_2, {
            opacity: 1,
            duration: 0.3,
            ease: "power2.out"
          });
        }
      }
    };
    $[6] = animateClose;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleTouchEnd = t6;
  let t7;
  if ($[8] !== handleTouchEnd) {
    t7 = () => {
      const closeHandler = closeHandlerRef.current;
      if (!closeHandler) {
        return;
      }
      const onTouchStart = e_1 => handleTouchStart(e_1);
      const onTouchMove = e_2 => handleTouchMove(e_2);
      const onTouchEnd = () => handleTouchEnd();
      closeHandler.addEventListener("touchstart", onTouchStart, {
        passive: false
      });
      closeHandler.addEventListener("touchmove", onTouchMove, {
        passive: false
      });
      closeHandler.addEventListener("touchend", onTouchEnd);
      return () => {
        closeHandler.removeEventListener("touchstart", onTouchStart);
        closeHandler.removeEventListener("touchmove", onTouchMove);
        closeHandler.removeEventListener("touchend", onTouchEnd);
      };
    };
    $[8] = handleTouchEnd;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useEffect(t7);
  let t8;
  if ($[10] !== animateClose) {
    t8 = <div ref={overlayRef} className={css.overlay} onClick={() => animateClose()} />;
    $[10] = animateClose;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div ref={closeHandlerRef} className={css.sheetHandle} />;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== animateClose) {
    t10 = () => animateClose();
    $[13] = animateClose;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Typography variant="body-m" tag="span">See results</Typography>;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10) {
    t12 = <button className={css.seeResultsButton} onClick={t10}>{t11}</button>;
    $[16] = t10;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== industries || $[19] !== onClearFilters || $[20] !== onToggleFilter || $[21] !== selectedIndustries || $[22] !== selectedServices || $[23] !== services || $[24] !== t12) {
    t13 = <div ref={filtersSheetRef} className={css.filtersSheet}>{t9}<div className={css.sheetContent}><Filters industries={industries} services={services} selectedIndustries={selectedIndustries} selectedServices={selectedServices} onClearFilters={onClearFilters} onToggleFilter={onToggleFilter}>{t12}</Filters></div></div>;
    $[18] = industries;
    $[19] = onClearFilters;
    $[20] = onToggleFilter;
    $[21] = selectedIndustries;
    $[22] = selectedServices;
    $[23] = services;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t8) {
    t14 = <>{t8}{t13}</>;
    $[26] = t13;
    $[27] = t8;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
};
export default MobileFiltersSheet;